export interface FilterDescriptionConfig<T = any> {
  key: string;
  labelSingular: string;
  labelPlural: string;
  canIncludeNA?: boolean;
  stringIds?: boolean;
  sourceList?: T[];
  name?: string;
  pmsNames?: string;
}

export function buildFilterDescriptionEntry<T = any>(
  routeParams: Record<string, string>,
  config: FilterDescriptionConfig<T>,
  pushTo: (entry: { name: string; tooltip?: string; additional?: number }) => void,
  additionalExcluded: string[] = []
): void {
  const rawValue = routeParams[config.key];
  if (!rawValue) return;

  const rawIds = rawValue
    .split(',')
    .map(id => id.trim())
    .filter(Boolean);

  const includesNA = config.canIncludeNA ? rawIds.includes('-1') : false;

  const includesEventHandler = rawIds.includes('EventHandler');

  const useStringIds = config.stringIds || rawIds.some(id => isNaN(Number(id)) && id !== '-1');
  const ids = useStringIds
    ? rawIds.filter(id => id !== '-1' && id !== 'EventHandler')
    : rawIds
        .filter(id => id !== '-1')
        .map(id => Number(id))
        .filter(id => !isNaN(id));

  const excludedKeys = [...additionalExcluded];

  const nameKeyBase = config.key.replace(/Ids?$/, '').replace(/Types$/, 'Type');
  const overrideKey = `${nameKeyBase}Names`;
  const overrideSingleKey = `${nameKeyBase}Name`;

  const nameOverride = !excludedKeys.includes(config.key)
    ? routeParams[overrideKey] || routeParams[overrideSingleKey]
    : null;

  let names: string[] = [];

  if (nameOverride) {
    names = nameOverride
      .split(',')
      .map(name => name.trim())
      .filter(Boolean);
  } else {
    names = ids.map((id: string | number) => (config.name ? config.name : id.toString())).filter(Boolean);
  }

  if (includesEventHandler && config.pmsNames) names.unshift(config.pmsNames);

  if (includesNA) {
    names.unshift('N/A');
  }

  const totalCount = names.length;
  if (totalCount === 0) return;

  const [first] = names;
  const tooltip = names.join('\n');
  const additional = totalCount > 1 ? totalCount - 1 : undefined;

  pushTo({
    name: totalCount > 1 ? `${config.labelPlural}: ${first}` : `${config.labelSingular}: ${first}`,
    ...(additional !== undefined && { tooltip, additional }),
  });
}
